/**
 * Authentication helper functions for consuming the shared oauth2 'AuthClient' class
 */
import {
  AuthClient,
  AppLogger,
  LogTypes,
  DatadogClient,
} from '@workivate/tho-web-shared';
import { getToken } from 'state/session/helpers';
import { get as storageGet } from 'globals/storage';
import { isUnifiedLoginEnabled } from 'globals/testable';
import { getStoredSelectedEnvironment } from 'react-components/env-controller/env-controller-context';

/**
 * Gets the authentication header for the current user
 * Can be Wam-Token or Bearer (JWT)
 * If the smartRefresh fails, it will log the user out
 */
// AuthHeader can be an empty object OR must contain either Authorization or Wam-Token
type AuthHeader =
  | Record<string, never>
  | { Authorization: string }
  | { 'Wam-Token': string };

const setWamTokenHeader = (header: AuthHeader): void => {
  const wamToken = getToken();
  if (wamToken) {
    header['Wam-Token'] = wamToken;
  }
};

export const getAuthenticationHeader = async (): Promise<AuthHeader> => {
  const header = {};

  // special case for limited account creation
  // we need to send the Wam-Token to fetch consents for the new user
  // not the shared user who is currently logged in
  // this will be removed when Limited Account creation moves into the USL FE
  if (storageGet('isCreatingLimitedAccount')) {
    setWamTokenHeader(header);
  } else if (isUnifiedLoginEnabled()) {
    try {
      // We need to check with every call how close we are to the token expiration
      // see REFRESH_TOKEN_BUFFER in authClient.ts
      const tokens = await AuthClient.smartRefresh();

      if (tokens?.idToken) {
        const jwtToken = tokens.idToken.getJwtString();
        header['Authorization'] = `Bearer ${jwtToken}`;
      }
    } catch (getJwtTokenError) {
      // if refresh fails, AuthEvents.subscribe in run-with-usl.tsx will handle logout
      // here we just want to log the reason
      AppLogger.logWithOptions({
        message: 'Error in getAuthenticationHeader',
        level: LogTypes.Error,
        error: getJwtTokenError,
        shouldLogToDatadog: true,
      });
    }
  } else {
    setWamTokenHeader(header);
  }

  return header;
};

/**
 * This function sets the clientId to the global context of DatadogClient
 * This means that the logs will be associated with the clientId
 * @param clientId
 */
export const setClientIdToGlobalContext = (clientId: string) => {
  DatadogClient.setGlobalContextProperty('public_client_id', clientId);
};

export const appendTestEnvironmentConfigParams = ({
  queryParams,
  configKey,
  configValue = localStorage.getItem(configKey),
}: {
  queryParams: URLSearchParams | string[];
  configKey: string;
  configValue?: string | null;
}): void => {
  if (configValue !== null && configValue !== undefined) {
    if (queryParams instanceof URLSearchParams) {
      queryParams.append(configKey, configValue);
    } else if (Array.isArray(queryParams)) {
      queryParams.push(`${configKey}=${configValue}`);
    }
  }
};

/**
 * Appends test environment configuration parameters to query params.
 * These are configuration parameters that can be turned on/off only in test environments.
 * It's important to pass these along when navigating to a different subdomain
 */
export const appendAllTestEnvironmentConfigParams = ({
  queryParams,
}: {
  queryParams: URLSearchParams | string[];
}): void => {
  if (!IS_PRODUCTION) {
    const storedEnvironment = getStoredSelectedEnvironment();

    // Append debug configuration parameters
    appendTestEnvironmentConfigParams({
      queryParams,
      configKey: 'isUnifiedLoginEnabled',
    });
    appendTestEnvironmentConfigParams({
      queryParams,
      configKey: 'dataDogEnabled',
    });

    appendTestEnvironmentConfigParams({
      queryParams,
      configKey: 'debugLogsOn',
    });
    appendTestEnvironmentConfigParams({
      queryParams,
      configKey: 'setEnvironment',
      configValue: storedEnvironment,
    });

    // Append enabled log features
    const enabledLogFeatures = localStorage.getItem('enabledLogFeatures');
    if (enabledLogFeatures) {
      try {
        const enabledLogFeaturesArray = JSON.parse(enabledLogFeatures);
        if (queryParams instanceof URLSearchParams) {
          queryParams.append(
            'enabledLogFeatures',
            JSON.stringify(enabledLogFeaturesArray),
          );
        } else if (Array.isArray(queryParams)) {
          queryParams.push(
            `enabledLogFeatures=${JSON.stringify(enabledLogFeaturesArray)}`,
          );
        }
      } catch (error) {
        // Handle JSON parse error gracefully - don't append invalid data
        console.warn(
          'Failed to parse enabledLogFeatures from localStorage:',
          error,
        );
      }
    }
  }
};
